/**
 * Centralized caching utilities for API responses and tokens
 * Uses localStorage for persistence with TTL (Time To Live) support
 * Follows Microsoft security best practices for client-side storage
 */

export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
  version: string; // Cache version for invalidation
}

export interface CacheConfig {
  defaultTtl: number;
  maxEntries: number;
  version: string;
  storagePrefix: string;
}

/**
 * Default cache configuration
 */
export const DEFAULT_CACHE_CONFIG: CacheConfig = {
  defaultTtl: 5 * 60 * 1000, // 5 minutes
  maxEntries: 100,
  version: '1.0',
  storagePrefix: 'lexai_cache_'
};

/**
 * Cache keys for different types of data
 */
export const CACHE_KEYS = {
  TOKEN_REFRESH: 'token_refresh',
  GRAPH_API_RESPONSE: 'graph_api_response',
  USER_PROFILE: 'user_profile',
  TASKS_LIST: 'tasks_list',
  EMAILS_LIST: 'emails_list',
  FILESYSTEM_FOLDERS: 'filesystem_folders'
} as const;

/**
 * Enhanced localStorage cache with TTL and size management
 */
export class CacheManager {
  private config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = { ...DEFAULT_CACHE_CONFIG, ...config };
  }

  /**
   * Generates a cache key with prefix
   */
  private getCacheKey(key: string): string {
    return `${this.config.storagePrefix}${key}`;
  }

  /**
   * Stores data in cache with TTL
   */
  set<T>(key: string, data: T, ttl: number = this.config.defaultTtl): void {
    try {
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        ttl,
        version: this.config.version
      };

      const cacheKey = this.getCacheKey(key);
      localStorage.setItem(cacheKey, JSON.stringify(entry));

      // Clean up old entries if we exceed max entries
      this.cleanup();
    } catch (error) {
      console.warn('Cache write failed:', error);
      // Silently fail - caching should not break the application
    }
  }

  /**
   * Retrieves data from cache if not expired
   */
  get<T>(key: string): T | null {
    try {
      const cacheKey = this.getCacheKey(key);
      const stored = localStorage.getItem(cacheKey);

      if (!stored) {
        return null;
      }

      const entry: CacheEntry<T> = JSON.parse(stored);

      // Check version compatibility
      if (entry.version !== this.config.version) {
        this.delete(key);
        return null;
      }

      // Check if entry has expired
      if (Date.now() - entry.timestamp > entry.ttl) {
        this.delete(key);
        return null;
      }

      return entry.data;
    } catch (error) {
      console.warn('Cache read failed:', error);
      // Clean up corrupted entry
      this.delete(key);
      return null;
    }
  }

  /**
   * Deletes a specific cache entry
   */
  delete(key: string): void {
    try {
      const cacheKey = this.getCacheKey(key);
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn('Cache delete failed:', error);
    }
  }

  /**
   * Clears all cache entries
   */
  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.config.storagePrefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Cache clear failed:', error);
    }
  }

  /**
   * Clears cache entries matching a pattern
   */
  clearPattern(pattern: string): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.config.storagePrefix) && key.includes(pattern)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Cache clear pattern failed:', error);
    }
  }

  /**
   * Gets cache statistics
   */
  getStats(): { entries: number; size: number } {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.config.storagePrefix));

      let totalSize = 0;
      cacheKeys.forEach(key => {
        const item = localStorage.getItem(key);
        if (item) {
          totalSize += item.length;
        }
      });

      return {
        entries: cacheKeys.length,
        size: totalSize
      };
    } catch (error) {
      console.warn('Cache stats failed:', error);
      return { entries: 0, size: 0 };
    }
  }

  /**
   * Cleans up expired entries and enforces max entries limit
   */
  private cleanup(): void {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.config.storagePrefix));

      if (cacheKeys.length <= this.config.maxEntries) {
        return;
      }

      // Get all entries with their timestamps
      const entries: Array<{ key: string; timestamp: number }> = [];

      cacheKeys.forEach(key => {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const entry = JSON.parse(stored);
            entries.push({
              key,
              timestamp: entry.timestamp
            });
          }
        } catch {
          // Remove corrupted entries
          localStorage.removeItem(key);
        }
      });

      // Sort by timestamp (oldest first) and remove oldest entries
      entries.sort((a, b) => a.timestamp - b.timestamp);

      const entriesToRemove = entries.slice(0, entries.length - this.config.maxEntries + 1);
      entriesToRemove.forEach(entry => {
        localStorage.removeItem(entry.key);
      });
    } catch (error) {
      console.warn('Cache cleanup failed:', error);
    }
  }

  /**
   * Checks if a cache entry exists and is valid
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Gets or sets cache entry with a factory function
   */
  async getOrSet<T>(
    key: string,
    factory: () => Promise<T>,
    ttl: number = this.config.defaultTtl
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    const data = await factory();
    this.set(key, data, ttl);
    return data;
  }
}

// Create a singleton cache manager instance
export const cacheManager = new CacheManager();

/**
 * Specialized token cache for authentication tokens
 */
export class TokenCache {
  private static readonly TOKEN_KEY = 'auth_token';
  private static readonly REFRESH_KEY = 'auth_refresh';
  private static readonly TOKEN_TTL = 50 * 60 * 1000; // 50 minutes (tokens typically last 1 hour)
  private static readonly REFRESH_TTL = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Caches an access token with expiration
   */
  static setToken(token: string, expiresOn?: Date): void {
    const ttl = expiresOn
      ? Math.max(0, expiresOn.getTime() - Date.now() - (5 * 60 * 1000)) // 5 min buffer
      : this.TOKEN_TTL;

    cacheManager.set(this.TOKEN_KEY, { token, expiresOn }, ttl);
  }

  /**
   * Retrieves a cached access token if valid
   */
  static getToken(): { token: string; expiresOn?: Date } | null {
    return cacheManager.get(this.TOKEN_KEY);
  }

  /**
   * Caches refresh token result for faster subsequent refreshes
   */
  static setRefreshResult(result: any): void {
    cacheManager.set(this.REFRESH_KEY, result, this.REFRESH_TTL);
  }

  /**
   * Retrieves cached refresh result
   */
  static getRefreshResult(): any | null {
    return cacheManager.get(this.REFRESH_KEY);
  }

  /**
   * Clears all cached tokens
   */
  static clear(): void {
    cacheManager.delete(this.TOKEN_KEY);
    cacheManager.delete(this.REFRESH_KEY);
  }

  /**
   * Checks if token is cached and valid
   */
  static hasValidToken(): boolean {
    const cached = this.getToken();
    return cached !== null && (!cached.expiresOn || cached.expiresOn.getTime() > Date.now());
  }
}

/**
 * Creates a cached version of an async function
 */
export function withCache<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  keyGenerator: (...args: T) => string,
  ttl: number = DEFAULT_CACHE_CONFIG.defaultTtl
): (...args: T) => Promise<R> {
  return async (...args: T): Promise<R> => {
    const cacheKey = keyGenerator(...args);
    return cacheManager.getOrSet(cacheKey, () => fn(...args), ttl);
  };
}

/**
 * Cache invalidation helpers
 */
export const CacheInvalidation = {
  /**
   * Invalidates all user-related cache entries
   */
  invalidateUserData: () => {
    cacheManager.clearPattern('user_');
    TokenCache.clear();
  },

  /**
   * Invalidates all Graph API related cache entries
   */
  invalidateGraphData: () => {
    cacheManager.clearPattern('graph_');
  },

  /**
   * Invalidates all filesystem related cache entries
   */
  invalidateFilesystemData: () => {
    cacheManager.clearPattern('filesystem_');
  },

  /**
   * Invalidates all cache entries (nuclear option)
   */
  invalidateAll: () => {
    cacheManager.clear();
  }
};
